import type { ContentBlock, IAttachment } from '../models';

// Legge un file come base64 puro (senza prefisso data URL).
export const readFileB64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (): void => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = (): void => reject(new Error('Impossibile leggere il file.'));
    r.readAsDataURL(file);
  });

// Blocco "document" per i PDF, "image" per le immagini.
export const fileToBlock = (f: IAttachment): ContentBlock => {
  const source = { type: 'base64' as const, media_type: f.mediaType, data: f.b64 };
  return f.mediaType === 'application/pdf'
    ? { type: 'document', source }
    : { type: 'image', source };
};

// Blob → base64 puro (senza prefisso data URL), stesso contratto di readFileB64.
export const blobToB64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (): void => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = (): void => reject(new Error('Impossibile leggere il file.'));
    r.readAsDataURL(blob);
  });

// base64 puro → Blob: serve per caricare su Supabase Storage un allegato letto in memoria.
export const b64ToBlob = (b64: string, mediaType: string): Blob => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return new Blob([bytes], { type: mediaType });
};

// Scarica un'immagine remota (es. URL pubblico Supabase Storage) e la converte in base64
// puro, stesso contratto di readFileB64: serve per allegarla come ContentBlock al prompt AI.
export const urlToB64 = async (url: string): Promise<{ data: string; mediaType: string }> => {
  const blob = await (await fetch(url)).blob();
  const data = await blobToB64(blob);
  return { data, mediaType: blob.type || 'image/jpeg' };
};

export const fmtSize = (n: number): string =>
  n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';

// pdf-lib (PDFDocument.save()) restituisce Uint8Array<ArrayBufferLike>: il .buffer è tipato come
// ArrayBuffer|SharedArrayBuffer, ma BlobPart vuole solo ArrayBuffer — da qui il cast, sicuro perché
// un Uint8Array creato da pdf-lib non è mai backed da uno SharedArrayBuffer a runtime.
export const pdfBytesToBlob = (bytes: Uint8Array): Blob =>
  new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' });

// Scarica un Blob come file, via link temporaneo. Unico punto per ogni export (.docx, PDF, CSV).
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
